import Reveal from "../Reveal";
import { ArrowUpRight, MessageSquare, Phone } from "lucide-react";
import { useBooking } from "../../lib/booking";

export default function Visit({ settings }) {
  const { openBooking } = useBooking();
  if (!settings) return null;
  const smsHref = `sms:${settings.phone_digits}`;
  const telHref = `tel:${settings.phone_digits}`;

  return (
    <section
      id="visit"
      data-testid="visit"
      style={{ padding: "8rem 0 6rem", background: "var(--fs-surface-deep)", borderTop: "1px solid var(--fs-line)" }}
    >
      <div className="fs-container grid md:grid-cols-12 gap-12 md:gap-16">
        <Reveal className="md:col-span-7">
          <p className="fs-overline" data-testid="visit-overline">THE ADDRESS</p>
          <h2 className="fs-display-2" style={{ marginTop: "1rem", marginBottom: "2.5rem" }} data-testid="visit-title">
            Reserve.<br />
            <span className="fs-ital">Any hour.</span>
          </h2>

          <a
            href={smsHref}
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 300,
              letterSpacing: "-0.02em",
              fontSize: "clamp(2.5rem, 7vw, 5rem)",
              lineHeight: 1,
              color: "var(--fs-caramel)",
              marginBottom: "0.5rem",
              textDecoration: "none",
              display: "block",
            }}
            data-testid="visit-phone-display"
          >
            {settings.phone_display}
          </a>
          <div className="fs-overline" style={{ color: "var(--fs-muted)", fontWeight: 600 }}>
            Text only · we'll respond fast
          </div>

          <div className="flex flex-wrap gap-4" style={{ marginTop: "2.25rem" }}>
            <button type="button" onClick={openBooking} className="fs-btn-primary" data-testid="visit-book-cta">
              <ArrowUpRight size={14} /> Reserve a Chair
            </button>
            <a href={smsHref} className="fs-btn-ghost" data-testid="visit-text-cta">
              <MessageSquare size={14} /> Text Us
            </a>
          </div>

          <div
            data-testid="visit-after-hours-note"
            style={{
              marginTop: "2rem",
              padding: "1.1rem 1.25rem",
              border: "1px solid var(--fs-caramel-line)",
              background: "rgba(184,137,60,0.06)",
              maxWidth: "560px",
            }}
          >
            <p className="fs-overline" style={{ color: "var(--fs-caramel)", marginBottom: "0.5rem" }}>
              After-hours requests
            </p>
            <p style={{ color: "var(--fs-ink-soft)", fontSize: "0.92rem", lineHeight: 1.6, margin: 0 }}>
              Online booking covers any hour with at least 24 hours' notice. For times outside
              normal business hours or last-minute requests, text your stylist directly —
              no phone calls accepted.
            </p>
          </div>

          <dl
            className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-7"
            style={{ marginTop: "3.5rem", borderTop: "1px solid var(--fs-line-strong)", paddingTop: "2.5rem" }}
          >
            <DLItem term="Address" value={settings.address_line} testid="visit-address" />
            <DLItem term="Hours" value={settings.hours_line} testid="visit-hours" italic />
            <DLItem term="Booking" value="Through Square Appointments" testid="visit-booking-label" />
            <DLItem term="Deposit" value={settings.deposit_note} testid="visit-deposit" />
          </dl>
        </Reveal>

        <Reveal className="md:col-span-5" delay={120}>
          <div
            style={{
              border: "1px solid var(--fs-caramel-line)",
              overflow: "hidden",
              aspectRatio: "4/5",
            }}
          >
            <img
              src={settings.visit_image_url}
              alt="Friends Salon 24 interior"
              data-testid="visit-image"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function DLItem({ term, value, italic, testid }) {
  return (
    <div data-testid={testid}>
      <dt
        className="fs-overline"
        style={{ color: "var(--fs-caramel)", fontWeight: 600, marginBottom: "0.5rem" }}
      >
        {term}
      </dt>
      <dd
        style={{
          margin: 0,
          fontFamily: "'Cormorant Garamond', serif",
          fontWeight: 300,
          fontSize: "clamp(1.25rem, 2vw, 1.6rem)",
          color: "var(--fs-ink)",
          lineHeight: 1.3,
        }}
      >
        {italic ? <span className="fs-ital">{value}</span> : value}
      </dd>
    </div>
  );
}
